import { ChangeDetectorRef, Directive, DoCheck, Input, OnChanges, OnDestroy, OnInit, ViewChild, Optional } from '@angular/core';
import { NgForm } from '@angular/forms';
import { CheckoutStepComponentDef } from '@df/checkout/checkout-step.component.def';
import { Checkout } from '@df/checkout/checkout.interfaces';
import { Events } from '@df/core/service/event';
import { NgInject } from '@df/ng/ng-inject';
import { CreditCard } from '@df/session/api/credit-cards/credit-card';
import { Subscription } from 'rxjs';

@Directive()
export abstract class CheckoutVaultCardComponentDef implements OnInit, OnChanges, OnDestroy, DoCheck {
    private _isSelected = false;
    /**
     * Card method model
     * BINDING
     * @type {CreditCard | undefined}
     */
    @Input()
    card?: CreditCard;

    /**
     * Cvc model
     * @type {string | undefined}
     */
    cvc?: string;

    @NgInject(Events)
    private events: Events;

    protected subscriptions = new Subscription();

    protected _stepForm?: NgForm;
    @ViewChild('stepForm', { static: false })
    set stepForm(form: NgForm) {
        if (this.checkoutStepcomponent) {
            this.checkoutStepcomponent.removeForm(this._stepForm);
            this.checkoutStepcomponent.addForm(form);
        }
        this._stepForm = form;
    }

    constructor(
        protected checkoutFormComponent: Checkout.ICheckoutForPaymentComponent,
        protected _ref: ChangeDetectorRef,
        @Optional() protected checkoutStepcomponent?: CheckoutStepComponentDef
    ) {}

    ngOnInit() {
        this.subscriptions.add(
            this.events.on(Checkout.EVENTS.SubmitBefore).subscribe((submitBeforeEventData: Checkout.ISubmitBeforeEventData) => {
                if (this.isSelected()) {
                    this.onCheckoutSubmit(submitBeforeEventData);
                }
            })
        );
        this.subscriptions.add(
            this.events.on(Checkout.EVENTS.SavePaymentMethodBefore).subscribe((savePaymentData: Checkout.ISavePaymentMethodData) => {
                if (savePaymentData.method === this.card?.code) {
                    savePaymentData.method = 'vault';
                    savePaymentData.additional_data = {
                        public_hash: this.card?.data.public_hash
                    };
                }
            })
        );
    }

    ngDoCheck() {
        const isSelected = this.isSelected();
        if (isSelected !== this._isSelected) {
            this._isSelected = isSelected;
            this._ref.detectChanges();
        }
    }

    ngOnChanges() {
        this.cvc = '';
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
        this.checkoutStepcomponent?.removeForm(this._stepForm);
    }

    /**
     * Checks if method is currently selected one
     * @return {boolean}
     */
    isSelected(): boolean {
        return this.getMethodCode() === this.checkoutFormComponent.paymentMethodCode;
    }

    /**
     * Returns if ccv code is required
     * @returns {boolean}
     */
    isCcvRequired(): boolean {
        return !!this.card?.data?.ccv_is_required;
    }

    /**
     * Returns method code
     * @type {string | number | undefined}
     */
    getMethodCode(): undefined | string | number {
        return this.card?.code;
    }

    protected onCheckoutSubmit(checkoutSubmiBeforeEventData: Checkout.ISubmitBeforeEventData): false | void {
        if (!this.checkoutFormComponent.status.busy) {
            const submissionData = this.getVaultCardMethodData();
            if (submissionData) {
                checkoutSubmiBeforeEventData.data.paymentMethod = submissionData;
            }
        }
    }

    /**
     * Returns payment method data
     * @returns {Checkout.ISubmissionPaymentVaultData | undefined}
     */
    protected getVaultCardMethodData(): Checkout.ISubmissionPaymentVaultData | undefined {
        if (this.card) {
            const paymentData: Checkout.ISubmissionPaymentVaultData = {
                method: 'vault',
                additional_data: {
                    public_hash: this.card.data.public_hash
                }
            };
            if (this.isCcvRequired()) {
                paymentData.additional_data.cvc = this.cvc;
            }
            return paymentData;
        }
        return undefined;
    }
}
